<script setup>
import { router } from '@inertiajs/vue3'
import { useToast } from 'primevue/usetoast'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AdminTabsLayout from '@/Layouts/AdminTabsLayout.vue'
import AppLayout from '@/Layouts/AppLayout.vue'

defineOptions({ layout: [AppLayout, AdminTabsLayout] })

defineProps({
  flags: { type: Array, required: true },
})

const { t } = useI18n()
const toast = useToast()

// Per-flag in-flight guard so the switch can't be double-toggled mid-request.
const saving = ref(null)

// PrimeVue v4 ToggleSwitch emits only `update:modelValue` (no `@change`), so we
// pass the new value straight through instead of binding v-model to a prop.
const toggleGlobal = (key, enabled) => {
  saving.value = key

  router.post(
    route('admin.feature-flag.update-global'),
    { key, enabled },
    {
      preserveScroll: true,
      onSuccess: () => {
        toast.add({
          severity: 'success',
          summary: t('messages.admin.feature_flags.updated'),
          life: 2000,
        })
      },
      onError: () => {
        toast.add({
          severity: 'error',
          summary: t('messages.error'),
          detail: t('messages.admin.feature_flags.update_error'),
          life: 3000,
        })
      },
      onFinish: () => {
        saving.value = null
      },
    },
  )
}
</script>

<template>
  <div class="max-w-3xl space-y-4 py-4">
    <div>
      <h2 class="text-xl font-medium text-gray-800">
        {{ $t('messages.admin.feature_flags.title') }}
      </h2>
      <p class="mt-1 text-sm text-gray-500">
        {{ $t('messages.admin.feature_flags.intro') }}
      </p>
    </div>

    <div class="divide-y divide-gray-200 rounded-lg border">
      <div
        v-for="flag in flags"
        :key="flag.key"
        class="flex items-center justify-between gap-6 px-4 py-3"
      >
        <div class="min-w-0">
          <div class="font-medium text-gray-800">{{ flag.label }}</div>
          <div class="text-sm text-gray-500">{{ flag.description }}</div>
          <div class="mt-1 flex items-center gap-3">
            <code class="text-xs text-gray-400">{{ flag.key }}</code>
            <span v-if="flag.overrides_count > 0" class="text-xs text-gray-400">
              {{ $t('messages.admin.feature_flags.overrides_count', { count: flag.overrides_count }) }}
            </span>
          </div>
        </div>

        <ToggleSwitch
          :modelValue="flag.global"
          :disabled="saving === flag.key"
          @update:modelValue="val => toggleGlobal(flag.key, val)"
        />
      </div>
    </div>
  </div>
</template>
